import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';

interface FileInputSignature {
  Element: HTMLInputElement;
  Args: {
    accept?: string;
    multiple?: boolean;
    onId?: (id: string) => void;
    onLoadFile?: (file: File) => void;
    onLoadDataUrl?: (dataUrl: string | ArrayBuffer | null) => void;
    onError?: (error: DOMException | null) => void;
    onLoadBinary?: (binary: string | ArrayBuffer | null) => void;
  };
}

export default class FileInput extends Component<FileInputSignature> {
  elementId = guidFor(this);

  @action
  inserted({ id: elementId }: HTMLInputElement): void {
    // We might need access to this element's ID in the outer context to give it a label[for="this_id"]
    // Passing in an ID when invoking this component seems to break FileField
    this.args.onId?.(elementId);
  }

  @action
  onChange({ target: input }: Event): void {
    if (!(input instanceof HTMLInputElement)) return;

    if (!isEmpty(input.files)) {
      this.filesDidChange(input.files);
    }
  }

  filesDidChange(files: FileList | null): void {
    if (files && files[0]) {
      this.args.onLoadFile?.(files[0]);

      if (this.args.onLoadBinary) {
        const binaryReader = new FileReader();
        binaryReader.onloadend = () => {
          this.args.onLoadBinary!(binaryReader.result);
        };
        binaryReader.onerror = () => {
          this.args.onError?.(binaryReader.error);
        };
        binaryReader.readAsBinaryString(files[0]);
      }

      if (this.args.onLoadDataUrl) {
        const dataUrlReader = new FileReader();
        dataUrlReader.onloadend = () => {
          this.args.onLoadDataUrl!(dataUrlReader.result);
        };
        dataUrlReader.onerror = () => {
          this.args.onError?.(dataUrlReader.error);
        };
        dataUrlReader.readAsDataURL(files[0]);
      }
    }
  }

  <template>
    <input
      data-test-file-upload-input
      type="file"
      id={{this.elementId}}
      multiple={{@multiple}}
      ...attributes
      {{didInsert this.inserted}}
      {{on "change" this.onChange}}
    />
  </template>
}
